import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import {type PaginatedResult, normalizePagination} from "@/utils/pagination";

export type BlacklistEntryType =
  | "id_document"
  | "full_name"
  | "phone"
  | "email"
  | "bank_account"
  | "iban_clabe_cbu"
  | "card_partial"
  | "crypto_address"
  | "ip"
  | "device_fingerprint"
  | "sanctioned_country";

export type BlacklistSource = "manual" | "ofac" | "un" | "local_list";

export interface BlacklistEntry {
  id: string;
  entryType: BlacklistEntryType;
  value: string;
  reason: string;
  source: BlacklistSource;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BlacklistListResult = PaginatedResult<BlacklistEntry>;

interface BlacklistFilters {
  page?: number;
  limit?: number;
  entryType?: string;
  source?: string;
  isActive?: boolean;
  search?: string;
}

export interface CreateBlacklistBody {
  entryType: BlacklistEntryType;
  value: string;
  source: BlacklistSource;
  reason: string;
  isActive?: boolean;
}

export const getBlacklistService = async (
  filters: BlacklistFilters = {}
): Promise<TResponseRequest<BlacklistListResult>> => {
  const raw = await httpRequest.get<BlacklistEntry[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/blacklist`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getBlacklistEntryByIdService = async (id: string): Promise<TResponseRequest<BlacklistEntry>> => {
  return httpRequest.get<BlacklistEntry>({
    url: `${config.apiBaseUrl}/bo/blacklist/${encodeURIComponent(id)}`,
  });
};

export const createBlacklistEntryService = async (
  body: CreateBlacklistBody
): Promise<TResponseRequest<BlacklistEntry>> => {
  return httpRequest.post<BlacklistEntry>({
    url: `${config.apiBaseUrl}/bo/blacklist`,
    body,
  });
};

export const updateBlacklistEntryService = async (
  id: string,
  body: Partial<CreateBlacklistBody>
): Promise<TResponseRequest<BlacklistEntry>> => {
  return httpRequest.patch<BlacklistEntry>({
    url: `${config.apiBaseUrl}/bo/blacklist/${encodeURIComponent(id)}`,
    body,
  });
};

export const activateBlacklistEntryService = async (id: string): Promise<TResponseRequest<BlacklistEntry>> => {
  return httpRequest.patch<BlacklistEntry>({
    url: `${config.apiBaseUrl}/bo/blacklist/${encodeURIComponent(id)}/activate`,
    body: {},
  });
};

export const deactivateBlacklistEntryService = async (id: string): Promise<TResponseRequest<BlacklistEntry>> => {
  return httpRequest.patch<BlacklistEntry>({
    url: `${config.apiBaseUrl}/bo/blacklist/${encodeURIComponent(id)}/deactivate`,
    body: {},
  });
};

export const getBlacklistMatchesService = async (): Promise<TResponseRequest<unknown[]>> => {
  return httpRequest.get<unknown[]>({
    url: `${config.apiBaseUrl}/bo/blacklist/matches`,
  });
};
